import { Component, computed, ElementRef, HostListener, inject, signal } from '@angular/core';
import { Icon } from '../icon/icon';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../core/api/auth.service';
import { TranslatePipe } from '@ngx-translate/core';
import { TK } from '../../../core/i18n';

@Component({
  selector: 'app-user-menu',
  imports: [Icon, RouterLink, TranslatePipe],
  template: `
    <div class="user-menu-wrapper">
      <button
        type="button"
        class="user-menu-button"
        (click)="toggleDropdown()"
        [attr.aria-expanded]="isDropdownOpen()"
        aria-haspopup="true"
      >
        <app-icon name="user"></app-icon>
      </button>
      @if (isDropdownOpen()) {
        <div class="user-dropdown" role="menu">
          <a
            href="https://www.fraenkische.com/de-DE/contact/drainage-systems"
            target="_blank"
            role="menuitem"
            (click)="closeDropdown()"
            [innerHTML]="TK.header.dropdown.contactPerson | translate"
          ></a>
          <a
            [routerLink]="isUserDataDisabled() ? null : '/user/user-data'"
            role="menuitem"
            [attr.aria-disabled]="isUserDataDisabled()"
            [attr.tabindex]="isUserDataDisabled() ? -1 : 0"
            [class.disabled]="isUserDataDisabled()"
            (click)="onUserDataClick($event)"
            [innerHTML]="TK.header.dropdown.userData | translate"
          ></a>
          <a
            routerLink="/user/company-about"
            role="menuitem"
            (click)="closeDropdown()"
            [innerHTML]="TK.header.dropdown.aboutRigoplan | translate"
          ></a>
          <button
            (click)="logout()"
            role="menuitem"
            [innerHTML]="TK.header.dropdown.logout | translate"
          ></button>
        </div>
      }
    </div>
  `,
  styles: `
    .user-menu-wrapper {
      position: relative;

      .user-menu-button {
        display: flex;
        align-items: center;
        color: var(--color-text);

        &:hover,
        &[aria-expanded='true'] {
          color: var(--color-text-strong);
        }
      }

      .user-dropdown {
        position: absolute;
        display: flex;
        flex-direction: column;
        top: calc(100% + 0.75rem);
        right: 0;
        width: 10.5rem;
        background: white;
        border: 1px solid var(--color-border-light);
        border-radius: 0.5rem;
        z-index: 1000;
        box-shadow: 0 0.75rem 1.5rem rgba(0, 0, 0, 0.08);
        overflow: hidden;

        a,
        button {
          width: 100%;
          padding: 0.75rem 1rem;
          text-align: center;
          font-size: 1rem;
          border-bottom: 1px solid var(--color-border-light);
          transition: color 0.2s ease;
          color: var(--color-text);

          &:hover {
            background: var(--color-gray-100);
            color: var(--color-black);
          }

          &:last-child {
            border-bottom: none;
          }

          &.disabled {
            color: var(--color-text-light);
            cursor: not-allowed;

            &:hover,
            &:active {
              background: transparent;
              color: var(--color-text-light);
            }
          }
        }
      }
    }

    @media (max-width: 768px) {
      .user-dropdown {
        display: none;
      }
    }
  `,
})
export class UserMenu {
  private authService = inject(AuthService);
  private router = inject(Router);
  private elementRef = inject(ElementRef);

  isDropdownOpen = signal<boolean>(false);

  protected readonly isUserDataDisabled = computed(
    () => this.authService.isAnonymous() || this.authService.isInternalUser(),
  );

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isDropdownOpen()) return;
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.closeDropdown();
  }

  toggleDropdown(): void {
    this.isDropdownOpen.update((value) => !value);
  }

  closeDropdown(): void {
    this.isDropdownOpen.set(false);
  }

  protected onUserDataClick(event: MouseEvent): void {
    if (this.isUserDataDisabled()) {
      event.preventDefault();
      return;
    }
    this.closeDropdown();
  }

  logout(): void {
    this.closeDropdown();
    this.authService.logout().subscribe({
      next: () => this.finishLogout(),
      error: () => this.finishLogout(),
    });
  }

  private finishLogout(): void {
    this.authService.clear();
    this.router.navigate(['/login']);
  }

  protected readonly TK = TK;
}
